import { html, TemplateResult } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { Keys } from '../../../types/common/types';
import { InputEvents } from '../types';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { GzTooltip } from '../../gzTooltip';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { TooltipSeverity } from '../../gzTooltip/types';
import { ButtonColor } from '../../gzIconButton/types';

export class GzInputWebComponent<T extends string | number | undefined = string> extends FormFieldInput<T> {
  @property({ attribute: true, reflect: true })
  type;

  /**
   * If the label value is set on the input, it will be displayed within the input field.
   */
  @property({ type: String, attribute: true, reflect: true })
  label = '';

  @property({ attribute: true, reflect: true })
  value;

  @property({ attribute: true, reflect: true })
  inputWidth;

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  readonly = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  showValidIcon = false;

  /**
   * The active property controls input active visual state
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  active = false;

  @property({ type: Number, attribute: true, reflect: true })
  maxChars = 100;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasCounter = false;

  @query('.current-count')
  public currentCount!: HTMLElement;

  @query('.max-count')
  public maxCount!: HTMLElement;

  @query('input,textarea')
  protected inputElement!: HTMLInputElement;

  @query('#label')
  protected inputLabelRef!: HTMLElement;

  static get styles() {
    return styles;
  }

  public update(changedProperties) {
    super.update(changedProperties);

    if (this.disabled) {
      this.tabIndex = -1;
    } else {
      this.removeAttribute('tabIndex');
    }

    if (changedProperties.has('inputWidth')) {
      this.style.width = this.returnInputWidth();
      this.style.maxWidth = this.returnInputWidth();
    }
  }

  public inputFocus() {
    this.inputElement?.focus();
  }

  private returnInputWidth(): string {
    if (this.inputWidth === 'full') {
      return '100%';
    } else {
      return this.inputWidth + 'px';
    }
  }

  public setFormValue(value: T) {
    this.setValue(value);
  }

  public getFormValue(): T {
    return this.value;
  }

  public setValue(value: T): void {
    this.value = value;
    this.setInputElementValue(value);
    this.emitInputEvent(InputEvents.CHANGE);
  }

  protected renderInputElements() {}

  public clearInputValue(e: Event) {}

  protected setActive(value: boolean) {
    this.active = value;
  }

  protected setInputElementValue(value: T) {
    this.inputElement.value = value?.toString() || '';
    this.onUserInput();
  }

  protected onKeyUp(event: KeyboardEvent) {
    this.getActionFromKey(event);
  }

  protected onInputBlur() {
    this.handleInputBlur();
    this.setActive(false);
  }

  protected onUserInput() {
    this.value = this.inputElement.value;
  }

  protected handleInputEvent(event: Event) {
    this.onUserInput();
    this.emitInputEvent(InputEvents.CHANGE);
  }

  protected getActionFromKey(event: KeyboardEvent) {
    const { key } = event;

    switch (key) {
      case Keys.Escape:
        this.onInputBlur();
        break;

      default:
        this.onUserInput();
    }
  }

  protected handleInputBlur() {
    this.inputElement.blur();
  }

  protected emitInputEvent(eventName: string) {
    const event = new CustomEvent(eventName, {
      detail: {
        sourceEvent: this.inputElement.value,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });
    this.dispatchEvent(event);
  }

  protected keyCounter() {
    if (!this.hasCounter) {
      return;
    }

    this.currentCount.innerHTML = String((this.inputElement as HTMLInputElement).value.length);
    this.maxCount.innerHTML = String(this.maxChars);

    this.inputMaxRangeColor();
  }

  protected inputMaxRangeColor(maxChars = this.maxChars) {
    const currentValue = (this.inputElement as HTMLInputElement).value.length;

    if (currentValue >= maxChars) {
      elementClassListAdd(this, 'max-chars-reached');
    } else {
      elementClassListRemove(this, 'max-chars-reached');
    }
  }

  // Remove the tooltip if the input/textarea value is cleared
  protected handleInputChange() {
    if (isEmpty(this.inputElement.value)) {
      GzTooltip.hide();
    }
  }

  protected renderInvalidIcon(): undefined | TemplateResult {
    if (this.invalid) {
      return html`<gz-tooltip
        class="tooltip"
        tooltipText="${this.errorTooltipText}"
        tooltipClass="${this.errorTooltipText ? TooltipSeverity.ERROR : TooltipSeverity.NEUTRAL}"
        ><gz-icon name="ds-icon-notification_negative_S" size="24" color="${ButtonColor.NEGATIVE}"></gz-icon
      ></gz-tooltip>`;
    }
  }

  protected renderSuccessIcon(): undefined | TemplateResult {
    if (!this.invalid && this.showValidIcon) {
      return html`<gz-icon name="ds-icon-validation_checked" size="24" color="${ButtonColor.SUCCESS}"></gz-icon>`;
    }
  }

  protected renderCounter(): undefined | TemplateResult {
    if (!this.hasCounter) {
      return;
    }

    return html`
      <div class="counting-section" part="counter">
        <span class="current-count"></span>
        <span class="separator">/</span>
        <span class="max-count"></span>
      </div>
    `;
  }

  protected renderInputLabel(): TemplateResult | undefined {
    if (isEmpty(this.label)) {
      return;
    }

    return html`<span id="label" class="input--label">${this.label}</span>`;
  }

  protected renderClearButton(): TemplateResult {
    return html`
      <gz-icon-button
        id="deleteButton"
        @click="${this.clearInputValue}"
        class="input--button input--delete icon-button"
        icon="ds-icon-clear"
        size="24"
        fontsize="20"
        buttontype="square"
        buttonfill="quiet"
        buttoncolor="secondary"
      >
      </gz-icon-button>
    `;
  }
}
